import NotificationsIcon from "@mui/icons-material/Notifications";
import { Badge, IconButton, type SxProps, styled, type Theme } from "@mui/material";
import type React from "react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useNotificationsContext } from "../../contexts/NotificationsContext";
import { STATUS, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";

const StyledBadge = styled(Badge)(() => ({
	"& .MuiBadge-badge": {
		right: -3,
		top: 3,
		border: `2px solid ${SURFACE}`,
		padding: "0 4px",
		backgroundColor: STATUS.ERROR.main,
		color: "white",
		fontWeight: "bold",
	},
}));

interface NotificationBellProps {
	color?: "inherit" | "primary" | "secondary" | "default";
	size?: "small" | "medium" | "large";
	sx?: SxProps<Theme>;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ color = "inherit", size = "small", sx = {} }) => {
	const { unseenNotificationCount } = useNotificationsContext();
	const navigate = useNavigate();
	const { userInfo } = useAuth();
	const previousCount = useRef<number>(unseenNotificationCount);

	useEffect(() => {
		previousCount.current = unseenNotificationCount;
	}, [unseenNotificationCount]);

	const handleClick = () => {
		trackEvent("notifications_bell_clicked", {
			user_id: userInfo?.id,
			unseen_count: unseenNotificationCount,
			has_notifications: unseenNotificationCount > 0,
			timestamp: new Date().toISOString(),
			referrer: window.location.pathname,
			session_id: sessionStorage.getItem("session_id") || "unknown",
		});

		navigate("/notifications");
	};

	return (
		<IconButton
			data-testid="header-notification-bell"
			onClick={handleClick}
			color={color}
			size={size}
			sx={sx}
			aria-label={`${unseenNotificationCount} unread notifications`}
		>
			<StyledBadge
				badgeContent={unseenNotificationCount}
				invisible={unseenNotificationCount === 0}
				max={99} // Show 99+ for counts over 99
			>
				<NotificationsIcon fontSize={size} />
			</StyledBadge>
		</IconButton>
	);
};
